import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, AlertTriangle, Package, Truck, CheckCircle2, Clock, ChevronLeft, ChevronRight, Calendar, ChevronDown, ChevronUp, Train } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, ComposedChart, Line } from 'recharts';
import axios from 'axios';
import HitachiReport from '../../components/planning/HitachiReport';
import KPICard from './KPICard';
import KPIDetailModal from './KPIDetailModal';
import SectionHeader from './SectionHeader';
import CustomTooltip from './CustomTooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1', '#14b8a6', '#f97316'];

function TrainReport() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cutoffWeek, setCutoffWeek] = useState(null); // null = current week
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly'
  const [expandedPeriods, setExpandedPeriods] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/projects`);
        setProjects(res.data || []);
        const trainProject = res.data?.find(p => p.planning_unit === 'seat');
        if (trainProject) setSelectedProject(trainProject.id);
      } catch (err) { console.error(err); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/mdp/years?project_id=${selectedProject}`);
        setAvailableYears(res.data || []);
      } catch (err) { console.error(err); }
    })();
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    (async () => {
      try {
        let url = `${API}/api/mdp/report-data/${selectedProject}?year=${selectedYear}`;
        if (cutoffWeek) url += `&cutoff_week=${cutoffWeek}`;
        const res = await axios.get(url);
        setReportData(res.data);
        // Set cutoff week from response if not set
        if (!cutoffWeek && res.data?.cutoff_week) {
          setCutoffWeek(res.data.cutoff_week);
        }
        // Lazy load material detail in background for charts
        const detailUrl = url + '&include_detail=true';
        axios.get(detailUrl).then(detailRes => {
          setReportData(prev => prev ? { ...prev, material_detail: detailRes.data.material_detail } : prev);
        }).catch(() => {});
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [selectedProject, selectedYear, cutoffWeek]);

  const projectName = projects.find(p => p.id === selectedProject)?.name || '';
  const selectedProjectData = projects.find(p => p.id === selectedProject);
  const isHitachi = !!(selectedProjectData?.hitachi_seat_types?.length > 0);

  // Available weeks from data
  const availableWeeks = useMemo(() => {
    if (!reportData?.weekly) return [];
    return reportData.weekly.map(w => w.week);
  }, [reportData]);

  const weeklyChartData = useMemo(() => {
    if (!reportData?.weekly) return [];
    return reportData.weekly.map(w => ({
      name: `W${w.week}`,
      'MDP Demand': w.demand,
      'Produced': w.completed,
      'Shipped': w.shipped,
      'Shortage': w.shortage,
    }));
  }, [reportData]);

  const monthlyChartData = useMemo(() => {
    if (!reportData?.monthly) return [];
    return reportData.monthly.map(m => ({
      name: m.label,
      'MDP Demand': m.demand,
      'Produced': m.completed,
      'Shipped': m.shipped,
      'Shortage': m.shortage,
    }));
  }, [reportData]);

  const cumulativeChartData = useMemo(() => {
    if (!reportData?.weekly) return [];
    return reportData.weekly.map(w => ({
      name: `W${w.week}`,
      'Cum. Demand': w.cumulative_demand,
      'Cum. Produced': w.cumulative_completed,
      'Cum. Shipped': w.cumulative_shipped,
    }));
  }, [reportData]);

  const seatPieData = useMemo(() => {
    if (!reportData?.seats) return [];
    return reportData.seats.map(s => ({
      name: s.description.replace('Seat Type ', '').replace('Single Seat Type ', '1P-'),
      value: s.demand,
    }));
  }, [reportData]);

  // Seat-based shipment chart data (weekly & monthly)
  const seatShipmentData = useMemo(() => {
    if (!reportData?.material_detail || !reportData?.weekly) return { weekly: [], monthly: [], seatNames: [] };
    const md = reportData.material_detail;
    // Get all seat type names
    const seatMap = {};
    for (const wk of Object.keys(md)) {
      for (const m of md[wk]) {
        if (m.type === 'seat' && (m.shipped || 0) > 0) {
          const shortName = m.desc.replace('Seat Type ', '').replace('Single Seat Type ', '1P-');
          seatMap[shortName] = true;
        }
      }
    }
    const seatNames = Object.keys(seatMap).sort();

    // Weekly data
    const weekly = reportData.weekly.map(w => {
      const row = { name: `W${w.week}` };
      const materials = md[w.week_key] || [];
      for (const sn of seatNames) row[sn] = 0;
      for (const m of materials) {
        if (m.type !== 'seat') continue;
        const shortName = m.desc.replace('Seat Type ', '').replace('Single Seat Type ', '1P-');
        if (seatNames.includes(shortName)) row[shortName] = m.shipped || 0;
      }
      return row;
    }).filter(r => seatNames.some(sn => r[sn] > 0));

    // Monthly aggregation
    const months = {};
    const monthOrder = [];
    for (const w of reportData.weekly) {
      try {
        const d = new Date(w.year || reportData.year || 2026, 0, 1 + (w.week - 1) * 7);
        const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const ml = d.toLocaleString('en', { month: 'short' });
        if (!months[mk]) { months[mk] = { name: ml }; for (const sn of seatNames) months[mk][sn] = 0; monthOrder.push(mk); }
        const materials = md[w.week_key] || [];
        for (const m of materials) {
          if (m.type !== 'seat') continue;
          const shortName = m.desc.replace('Seat Type ', '').replace('Single Seat Type ', '1P-');
          if (seatNames.includes(shortName)) months[mk][shortName] += m.shipped || 0;
        }
      } catch {}
    }
    const monthly = monthOrder.map(mk => months[mk]).filter(r => seatNames.some(sn => r[sn] > 0));

    return { weekly, monthly, seatNames };
  }, [reportData]);

  const [shipViewMode, setShipViewMode] = useState('weekly');

  const s = reportData?.summary || {};
  const cw = reportData?.cutoff_week || cutoffWeek;
  const [detailModal, setDetailModal] = useState(null); // { title, color, data, columns }

  const materialCol = { key: 'desc', label: 'Material', render: (r) => (
    <div><div className="font-medium text-slate-700">{r.description || r.desc}</div><div className="text-[9px] text-slate-400">{r.code}</div></div>
  )};
  const spuCol = { key: 'spu', label: 'SPU', align: 'right', render: (r) => <span className="text-violet-600 font-semibold">{r.spu}P</span> };

  const showShortageDetail = (type) => {
    const seats = reportData?.seats || [];
    const field = type === 'prd' ? 'shortage' : type === 'shp' ? 'shortage_shp' : 'shortage_pln';
    const filtered = seats.filter(s => (s[field] || 0) > 0).map(s => ({
      ...s,
      shortageVal: s[field] || 0,
      sp: (s[field] || 0) * (s.spu || 1),
    })).sort((a, b) => b.shortageVal - a.shortageVal);
    const label = type === 'prd' ? 'S.PRD (Production Shortage)' : type === 'shp' ? 'S.SHP (Shipment Shortage)' : 'S.PLN (Planning Shortage)';
    setDetailModal({
      title: `${label} - W${cw} Detail`,
      color: type === 'pln' ? 'amber' : 'red',
      data: filtered,
      columns: [
        materialCol, spuCol,
        { key: 'cutoff_demand', label: `MDP (to W${cw})`, align: 'right', render: (r) => <span className="text-blue-600 font-medium">{(r.cutoff_demand || 0).toLocaleString()}</span> },
        { key: 'completed', label: 'Produced', align: 'right', render: (r) => <span className="text-emerald-600">{r.completed_all || r.completed || 0}</span> },
        { key: 'shipped', label: 'Shipped', align: 'right', render: (r) => <span className="text-teal-600">{r.shipped_all || r.shipped || 0}</span> },
        { key: 'shortageVal', label: 'Shortage (seat)', align: 'right', render: (r) => <span className="text-red-600 font-bold">{r.shortageVal}</span> },
        { key: 'sp', label: 'Shortage (sp)', align: 'right', render: (r) => <span className="text-red-400 font-semibold">{r.sp}</span> },
      ],
    });
  };

  const showShippedDetail = () => {
    const seats = reportData?.seats || [];
    const filtered = seats.filter(s => (s.shipped_all || s.shipped || 0) > 0).map(s => ({
      ...s,
      shippedVal: s.shipped_all || s.shipped || 0,
      shippedSp: (s.shipped_all || s.shipped || 0) * (s.spu || 1),
    })).sort((a, b) => b.shippedVal - a.shippedVal);
    setDetailModal({
      title: `Shipped Detail - W${cw}`,
      color: 'teal',
      data: filtered,
      columns: [
        materialCol, spuCol,
        { key: 'demand', label: 'MDP Demand', align: 'right', render: (r) => <span className="text-blue-600 font-medium">{(r.demand || 0).toLocaleString()}</span> },
        { key: 'completed', label: 'Produced', align: 'right', render: (r) => <span className="text-emerald-600">{r.completed_all || r.completed || 0}</span> },
        { key: 'shippedVal', label: 'Shipped (seat)', align: 'right', render: (r) => <span className="text-teal-700 font-bold">{r.shippedVal}</span> },
        { key: 'shippedSp', label: 'Shipped (sp)', align: 'right', render: (r) => <span className="text-teal-500 font-semibold">{r.shippedSp}</span> },
        { key: 'remaining', label: 'Remaining', align: 'right', render: (r) => {
          const rem = Math.max(0, (r.demand || 0) - (r.shipped_all || r.shipped || 0));
          return <span className={rem > 0 ? 'text-red-500 font-medium' : 'text-slate-300'}>{rem}</span>;
        }},
      ],
    });
  };

  const showProducedDetail = () => {
    const seats = reportData?.seats || [];
    const filtered = seats.filter(s => (s.completed_all || s.completed || 0) > 0).map(s => ({
      ...s,
      producedVal: s.completed_all || s.completed || 0,
      producedSp: (s.completed_all || s.completed || 0) * (s.spu || 1),
    })).sort((a, b) => b.producedVal - a.producedVal);
    setDetailModal({
      title: `Produced Detail - W${cw}`,
      color: 'emerald',
      data: filtered,
      columns: [
        materialCol, spuCol,
        { key: 'demand', label: 'MDP Demand', align: 'right', render: (r) => <span className="text-blue-600 font-medium">{(r.demand || 0).toLocaleString()}</span> },
        { key: 'producedVal', label: 'Produced (seat)', align: 'right', render: (r) => <span className="text-emerald-700 font-bold">{r.producedVal}</span> },
        { key: 'producedSp', label: 'Produced (sp)', align: 'right', render: (r) => <span className="text-emerald-500 font-semibold">{r.producedSp}</span> },
        { key: 'shipped', label: 'Shipped', align: 'right', render: (r) => <span className="text-teal-600">{r.shipped_all || r.shipped || 0}</span> },
      ],
    });
  };

  const toggleExpand = useCallback((key) => {
    setExpandedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Compute period data based on view mode
  const periodData = useMemo(() => {
    if (!reportData?.weekly) return [];
    if (viewMode === 'weekly') {
      return reportData.weekly.map(w => ({
        key: w.week_key,
        label: `W${w.week}`,
        week: w.week,
        demand: w.demand,
        seat_demand: w.seat_demand || 0,
        seat_demand_sp: w.seat_demand_sp || 0,
        other_demand: w.other_demand || 0,
        planned: w.planned,
        seat_planned: w.seat_planned || 0,
        seat_planned_sp: w.seat_planned_sp || 0,
        other_planned: w.other_planned || 0,
        completed: w.completed,
        seat_completed: w.seat_completed || 0,
        seat_completed_sp: w.seat_completed_sp || 0,
        other_completed: w.other_completed || 0,
        shipped: w.shipped,
        seat_shipped: w.seat_shipped || 0,
        seat_shipped_sp: w.seat_shipped_sp || 0,
        other_shipped: w.other_shipped || 0,
        cumulative_demand: w.cumulative_demand,
        cumulative_shipped: w.cumulative_shipped,
        shortage: w.shortage,
        shortage_sp: w.shortage_sp || 0,
        shortage_shp: w.shortage_shp || 0,
        shortage_pln: w.shortage_pln || 0,
        is_future: w.is_future,
        is_cutoff: w.week === cw,
        detail_keys: [w.week_key],
      }));
    }
    // Monthly aggregation
    const months = {};
    const monthOrder = [];
    for (const w of reportData.weekly) {
      try {
        const d = new Date(w.year, 0, 1 + (w.week - 1) * 7);
        const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const ml = d.toLocaleString('en', { month: 'short', year: 'numeric' });
        if (!months[mk]) {
          months[mk] = { key: mk, label: ml, demand: 0, seat_demand: 0, seat_demand_sp: 0, other_demand: 0,
            planned: 0, seat_planned: 0, seat_planned_sp: 0, other_planned: 0,
            completed: 0, seat_completed: 0, seat_completed_sp: 0, other_completed: 0,
            shipped: 0, seat_shipped: 0, seat_shipped_sp: 0, other_shipped: 0,
            cumulative_demand: 0, cumulative_shipped: 0, shortage: 0, shortage_sp: 0,
            shortage_shp: 0, shortage_pln: 0,
            is_future: true, is_cutoff: false, detail_keys: [] };
          monthOrder.push(mk);
        }
        months[mk].demand += w.demand;
        months[mk].seat_demand += (w.seat_demand || 0);
        months[mk].seat_demand_sp += (w.seat_demand_sp || 0);
        months[mk].other_demand += (w.other_demand || 0);
        months[mk].planned += w.planned;
        months[mk].seat_planned += (w.seat_planned || 0);
        months[mk].seat_planned_sp += (w.seat_planned_sp || 0);
        months[mk].other_planned += (w.other_planned || 0);
        months[mk].completed += w.completed;
        months[mk].seat_completed += (w.seat_completed || 0);
        months[mk].seat_completed_sp += (w.seat_completed_sp || 0);
        months[mk].other_completed += (w.other_completed || 0);
        months[mk].shipped += w.shipped;
        months[mk].seat_shipped += (w.seat_shipped || 0);
        months[mk].seat_shipped_sp += (w.seat_shipped_sp || 0);
        months[mk].other_shipped += (w.other_shipped || 0);
        months[mk].completed += w.completed;
        months[mk].shipped += w.shipped;
        months[mk].cumulative_demand = w.cumulative_demand;
        months[mk].cumulative_shipped = w.cumulative_shipped;
        months[mk].shortage = w.shortage;
        months[mk].shortage_sp = w.shortage_sp || 0;
        months[mk].shortage_shp = w.shortage_shp || 0;
        months[mk].shortage_pln = w.shortage_pln || 0;
        if (!w.is_future) months[mk].is_future = false;
        if (w.week === cw) months[mk].is_cutoff = true;
        months[mk].detail_keys.push(w.week_key);
      } catch (e) {}
    }
    return monthOrder.map(mk => months[mk]);
  }, [reportData, viewMode, cw]);

  // Get material detail for a period (merge multiple week keys for monthly)
  const getMaterialDetail = useCallback((detailKeys) => {
    const md = reportData?.material_detail;
    if (!md || !detailKeys?.length) return [];
    if (detailKeys.length === 1) return md[detailKeys[0]] || [];
    // For monthly: use the last week's detail (cumulative state at end of month)
    for (let i = detailKeys.length - 1; i >= 0; i--) {
      if (md[detailKeys[i]]) return md[detailKeys[i]];
    }
    return [];
  }, [reportData]);

  if (loading && !reportData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1400px] mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800" data-testid="reports-title">Executive Report</h1>
            <p className="text-xs text-slate-400">Production KPI Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedProject || ''} onChange={e => { setSelectedProject(e.target.value); setCutoffWeek(null); }}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 bg-white" data-testid="report-project-select">
            {projects.filter(p => p.planning_unit === 'seat').map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.sr_number ? ` (${p.sr_number})` : ''}</option>
            ))}
          </select>
          {!isHitachi && (
            <>
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                {availableYears.map(y => (
                  <button key={y} onClick={() => { setSelectedYear(y); setCutoffWeek(null); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${selectedYear === y ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid={`report-year-${y}`}>{y}</button>
                ))}
              </div>
              <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1 bg-white" data-testid="week-selector">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] text-slate-400 font-medium">Cutoff:</span>
                <button onClick={() => setCutoffWeek(Math.max(1, (cw || 1) - 1))}
                  className="p-0.5 hover:bg-slate-100 rounded" data-testid="week-prev">
                  <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <span className="text-xs font-bold text-blue-600 min-w-[32px] text-center" data-testid="cutoff-week-display">W{cw}</span>
                <button onClick={() => setCutoffWeek(Math.min(52, (cw || 1) + 1))}
                  className="p-0.5 hover:bg-slate-100 rounded" data-testid="week-next">
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Hitachi Report - completely different layout */}
      {isHitachi && selectedProject && (
        <HitachiReport projectId={selectedProject} projectName={projectName} />
      )}

      {/* Standard Train Report (Alstom NJT style) */}
      {!isHitachi && !reportData ? (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a project and year to view reports</p>
        </div>
      ) : !isHitachi ? (
        <>
          {/* KPI Cards - Row 1: Demand & Types */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="report-kpi-cards">
            <KPICard label="MDP Demand" value={s.total_demand_seats}
              badge="seat" subtitle={`${(s.total_seat_placed || 0).toLocaleString()} seat placed`}
              icon={<Package className="w-4 h-4" />} color="blue" />
            <KPICard label="Seat Types" value={s.seat_types}
              subtitle={`+ ${s.other_types} other components`}
              icon={<BarChart3 className="w-4 h-4" />} color="violet" />
            <KPICard label={`Planned (W${cw})`} value={s.total_planned_seats}
              badge="seat" subtitle={`${(s.total_planned_sp || 0).toLocaleString()} seat placed`}
              icon={<Clock className="w-4 h-4" />} color="amber" />
            <KPICard label={`Produced (W${cw})`} value={s.total_completed_seats}
              badge="seat" subtitle={`${(s.total_completed_sp || 0).toLocaleString()} seat placed`}
              icon={<CheckCircle2 className="w-4 h-4" />} color="emerald"
              onClick={() => showProducedDetail()} />
          </div>

          {/* KPI Cards - Row 2: Shipping & Shortage */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="report-kpi-cards-row2">
            <KPICard label={`Shipped (W${cw})`} value={s.total_shipped_seats}
              badge="seat" subtitle={`${(s.total_shipped_sp || 0).toLocaleString()} seat placed`}
              icon={<Truck className="w-4 h-4" />} color="teal"
              onClick={() => showShippedDetail()} />
            <KPICard label={`S.PRD (W${cw})`} value={s.shortage_seats}
              badge="seat" subtitle={`${(s.shortage_sp || 0).toLocaleString()} seat placed`}
              subtitle2={`2P: ${s.shortage_2p || 0} / 1P: ${s.shortage_1p || 0}`}
              icon={<AlertTriangle className="w-4 h-4" />} color="red"
              onClick={() => showShortageDetail('prd')} />
            <KPICard label={`S.SHP (W${cw})`} value={s.shortage_shipped_seats || 0}
              badge="seat" subtitle={`${(s.shortage_shipped_sp || 0).toLocaleString()} seat placed`}
              icon={<AlertTriangle className="w-4 h-4" />} color="red"
              onClick={() => showShortageDetail('shp')} />
            <KPICard label={`S.PLN (W${cw})`} value={s.shortage_pln_seats || 0}
              badge="seat" subtitle={`${(s.shortage_pln_sp || 0).toLocaleString()} seat placed`}
              icon={<AlertTriangle className="w-4 h-4" />} color="red"
              onClick={() => showShortageDetail('pln')} />
            <KPICard label="Remaining" value={s.remaining_seats}
              badge="seat" subtitle={`${(s.remaining_sp || 0).toLocaleString()} seat placed`}
              icon={<Package className="w-4 h-4" />} color="indigo" />
          </div>

          {/* KPI Detail Modal */}
          {detailModal && (
            <KPIDetailModal title={detailModal.title} color={detailModal.color}
              data={detailModal.data} columns={detailModal.columns}
              onClose={() => setDetailModal(null)} />
          )}

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="monthly-bar-chart">
              <SectionHeader title="Monthly Overview" subtitle="MDP Demand vs Production vs Shipping" />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyChartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="MDP Demand" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Produced" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Shipped" fill="#06b6d4" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="seat-pie-chart">
              <SectionHeader title="Seat Distribution" subtitle="MDP demand by seat type" />
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={seatPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={95}
                    paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ strokeWidth: 1, stroke: '#cbd5e1' }}>
                    {seatPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cumulative-area-chart">
              <SectionHeader title="Cumulative Trend" subtitle="Running totals: Demand vs Produced vs Shipped" />
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={cumulativeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="Cum. Demand" stroke="#3b82f6" fill="#3b82f620" strokeWidth={2} />
                  <Area type="monotone" dataKey="Cum. Produced" stroke="#10b981" fill="#10b98120" strokeWidth={2} />
                  <Area type="monotone" dataKey="Cum. Shipped" stroke="#06b6d4" fill="#06b6d420" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="shortage-trend-chart">
              <SectionHeader title="Shortage Trend" subtitle={`Weekly demand bars + cumulative shortage (cutoff: W${cw})`} />
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={weeklyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="MDP Demand" fill="#3b82f640" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="Shortage" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Seat-Based Shipment Chart */}
          {seatShipmentData.seatNames.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="seat-shipment-chart">
              <div className="flex items-center justify-between mb-4">
                <SectionHeader title="Shipments by Seat Type" subtitle={`${shipViewMode === 'weekly' ? 'Weekly' : 'Monthly'} breakdown - which seats shipped when`} />
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="ship-view-toggle">
                  {['weekly', 'monthly'].map(vm => (
                    <button key={vm} onClick={() => setShipViewMode(vm)}
                      className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all ${shipViewMode === vm ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                      {vm === 'weekly' ? 'Weekly' : 'Monthly'}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={shipViewMode === 'weekly' ? seatShipmentData.weekly : seatShipmentData.monthly} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#94a3b8' }} interval={shipViewMode === 'weekly' ? 1 : 0} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {seatShipmentData.seatNames.map((sn, i) => (
                    <Bar key={sn} dataKey={sn} stackId="shipped" fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Period Detail Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="weekly-detail-table">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div>
                <SectionHeader title={viewMode === 'weekly' ? 'Weekly Detail' : 'Monthly Detail'}
                  subtitle={`${projectName} - ${selectedYear} (Shortage cutoff: W${cw})`} />
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="view-mode-toggle">
                {['weekly', 'monthly'].map(vm => (
                  <button key={vm} onClick={() => { setViewMode(vm); setExpandedPeriods(new Set()); }}
                    className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all ${viewMode === vm ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    data-testid={`view-${vm}`}>
                    {vm === 'weekly' ? 'Weekly' : 'Monthly'}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                    <th className="px-3 py-2 text-left font-semibold w-8"></th>
                    <th className="px-3 py-2 text-left font-semibold">{viewMode === 'weekly' ? 'Week' : 'Month'}</th>
                    <th className="px-3 py-2 text-right font-semibold">MDP<br/><span className="text-[7px] font-normal normal-case text-blue-300">seat (sp) | other</span></th>
                    <th className="px-3 py-2 text-right font-semibold">Planned<br/><span className="text-[7px] font-normal normal-case text-amber-300">seat (sp) | other</span></th>
                    <th className="px-3 py-2 text-right font-semibold">Produced<br/><span className="text-[7px] font-normal normal-case text-emerald-300">seat (sp) | other</span></th>
                    <th className="px-3 py-2 text-right font-semibold">Shipped<br/><span className="text-[7px] font-normal normal-case text-teal-300">seat (sp) | other</span></th>
                    <th className="px-3 py-2 text-right font-semibold text-red-400">S.PRD<br/><span className="text-[7px] font-normal normal-case text-red-300">seat | sp</span></th>
                    <th className="px-3 py-2 text-right font-semibold text-red-400">S.SHP<br/><span className="text-[7px] font-normal normal-case text-red-300">seat</span></th>
                    <th className="px-3 py-2 text-right font-semibold text-orange-400">S.PLN<br/><span className="text-[7px] font-normal normal-case text-orange-300">seat</span></th>
                  </tr>
                </thead>
                <tbody>
                  {periodData.map((p, i) => {
                    const isExpanded = expandedPeriods.has(p.key);
                    const detail = isExpanded ? getMaterialDetail(p.detail_keys) : [];
                    const seatDetail = detail.filter(d => d.type === 'seat');
                    const otherDetail = detail.filter(d => d.type !== 'seat');
                    return (
                      <React.Fragment key={p.key}>
                        <tr className={`${p.is_cutoff ? 'bg-blue-50/60 border-l-2 border-l-blue-400' : i % 2 === 0 ? '' : 'bg-slate-50/30'} hover:bg-blue-50/30 transition-colors cursor-pointer select-none`}
                          onClick={() => toggleExpand(p.key)} data-testid={`period-row-${p.key}`}>
                          <td className="px-2 py-2 text-center">
                            {isExpanded
                              ? <ChevronUp className="w-3.5 h-3.5 text-blue-500 inline" />
                              : <ChevronDown className="w-3.5 h-3.5 text-slate-300 inline" />}
                          </td>
                          <td className={`px-3 py-2 font-semibold ${p.is_cutoff ? 'text-blue-700' : 'text-slate-700'}`}>
                            {p.label} {p.is_cutoff && <span className="text-[8px] bg-blue-200 text-blue-700 px-1 rounded ml-1">cutoff</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-blue-600 font-medium">
                            <div className="font-bold">{p.demand.toLocaleString()}</div>
                            {(p.seat_demand > 0 || p.other_demand > 0) && (
                              <div className="text-[8px]">
                                {p.seat_demand > 0 && <span className="text-violet-500 font-semibold">{p.seat_demand} seat{p.seat_demand_sp > 0 && <span className="text-violet-400"> ({p.seat_demand_sp}sp)</span>}</span>}
                                {p.seat_demand > 0 && p.other_demand > 0 && <span className="text-slate-300"> | </span>}
                                {p.other_demand > 0 && <span className="text-slate-400">{p.other_demand} other</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-amber-600">
                            <div className="font-bold">{p.planned || '-'}</div>
                            {(p.seat_planned > 0 || p.other_planned > 0) && (
                              <div className="text-[8px]">
                                {p.seat_planned > 0 && <span className="text-violet-500 font-semibold">{p.seat_planned} seat{p.seat_planned_sp > 0 && <span className="text-violet-400"> ({p.seat_planned_sp}sp)</span>}</span>}
                                {p.seat_planned > 0 && p.other_planned > 0 && <span className="text-slate-300"> | </span>}
                                {p.other_planned > 0 && <span className="text-slate-400">{p.other_planned} other</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-emerald-600">
                            <div className="font-bold">{p.completed || '-'}</div>
                            {(p.seat_completed > 0 || p.other_completed > 0) && (
                              <div className="text-[8px]">
                                {p.seat_completed > 0 && <span className="text-violet-500 font-semibold">{p.seat_completed} seat{p.seat_completed_sp > 0 && <span className="text-violet-400"> ({p.seat_completed_sp}sp)</span>}</span>}
                                {p.seat_completed > 0 && p.other_completed > 0 && <span className="text-slate-300"> | </span>}
                                {p.other_completed > 0 && <span className="text-slate-400">{p.other_completed} other</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-teal-600">
                            <div className="font-bold">{p.shipped || '-'}</div>
                            {(p.seat_shipped > 0 || p.other_shipped > 0) && (
                              <div className="text-[8px]">
                                {p.seat_shipped > 0 && <span className="text-violet-500 font-semibold">{p.seat_shipped} seat{p.seat_shipped_sp > 0 && <span className="text-violet-400"> ({p.seat_shipped_sp}sp)</span>}</span>}
                                {p.seat_shipped > 0 && p.other_shipped > 0 && <span className="text-slate-300"> | </span>}
                                {p.other_shipped > 0 && <span className="text-slate-400">{p.other_shipped} other</span>}
                              </div>
                            )}
                          </td>
                          <td className={`px-3 py-2 text-right ${p.shortage > 0 ? 'text-red-500' : 'text-slate-300'}`}>
                            {p.is_future ? '-' : (
                              <div>
                                <span className="font-semibold">{p.shortage.toLocaleString()}</span>
                                {p.shortage_sp > 0 && (
                                  <span className="text-[9px] text-red-300 ml-1">| {p.shortage_sp.toLocaleString()}</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className={`px-3 py-2 text-right ${(p.shortage_shp || 0) > 0 ? 'text-red-500 font-semibold' : 'text-slate-300'}`}>
                            {p.is_future ? '-' : (p.shortage_shp || 0).toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 text-right ${(p.shortage_pln || 0) > 0 ? 'text-orange-500 font-semibold' : 'text-slate-300'}`}>
                            {p.is_future ? '-' : (p.shortage_pln || 0).toLocaleString()}
                          </td>
                        </tr>
                        {/* Expanded material detail - same table columns */}
                        {isExpanded && detail.length > 0 && (
                          <>
                            {seatDetail.length > 0 && (
                              <tr className="bg-blue-50/30 border-t border-blue-100">
                                <td></td>
                                <td colSpan={8} className="px-3 py-1 text-[8px] font-bold text-blue-500 uppercase tracking-wider">
                                  Seats ({seatDetail.length})
                                </td>
                              </tr>
                            )}
                            {seatDetail.map((m, mi) => {
                              const pDates = (m.produced_dates || []).join(', ');
                              const sDates = (m.shipped_dates || []).join(', ');
                              return (
                              <tr key={`s-${m.code}`} className={`text-[10px] ${mi % 2 === 0 ? 'bg-blue-50/10' : 'bg-white'} hover:bg-blue-50/30`}>
                                <td></td>
                                <td className="px-3 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[8px] font-bold text-violet-500 bg-violet-50 px-1 py-0.5 rounded shrink-0">{m.spu}P</span>
                                    <div className="min-w-0">
                                      <div className="font-medium text-slate-700 truncate" title={m.desc}>{m.desc}</div>
                                      <div className="text-[8px] text-slate-400">{m.code}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-1.5 text-right text-blue-600 font-medium">{m.demand || '-'}</td>
                                <td className="px-3 py-1.5 text-right text-amber-600">{m.planned || '-'}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="text-emerald-600 font-medium">{m.completed || '-'}</span>
                                  {pDates && <div className="text-[8px] text-emerald-400">{pDates}</div>}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="text-teal-600 font-medium">{m.shipped || '-'}</span>
                                  {sDates && <div className="text-[8px] text-teal-400">{sDates}</div>}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_prd || 0) > 0 ? 'text-red-500 font-bold' : 'text-slate-300'}`}>
                                  {(m.shortage_prd || 0) > 0 ? (
                                    <div>{m.shortage_prd}<span className="text-[8px] text-red-300 ml-0.5">|{m.shortage_sp||0}</span></div>
                                  ) : '0'}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_shp || 0) > 0 ? 'text-red-500 font-bold' : 'text-slate-300'}`}>
                                  {m.shortage_shp || 0}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_pln || 0) > 0 ? 'text-orange-500 font-bold' : 'text-slate-300'}`}>
                                  {m.shortage_pln || 0}
                                </td>
                              </tr>
                              );
                            })}
                            {otherDetail.length > 0 && (
                              <tr className="bg-slate-50/50">
                                <td></td>
                                <td colSpan={8} className="px-3 py-1 text-[8px] font-bold text-slate-400 uppercase tracking-wider">
                                  Other Components ({otherDetail.length})
                                </td>
                              </tr>
                            )}
                            {otherDetail.map((m, mi) => {
                              const pDates = (m.produced_dates || []).join(', ');
                              const sDates = (m.shipped_dates || []).join(', ');
                              return (
                              <tr key={`o-${m.code}`} className={`text-[10px] ${mi % 2 === 0 ? 'bg-slate-50/20' : 'bg-white'} hover:bg-blue-50/30`}>
                                <td></td>
                                <td className="px-3 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[8px] font-bold text-slate-400 bg-slate-100 px-1 py-0.5 rounded shrink-0">Oth</span>
                                    <div className="min-w-0">
                                      <div className="font-medium text-slate-700 truncate" title={m.desc}>{m.desc}</div>
                                      <div className="text-[8px] text-slate-400">{m.code}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-1.5 text-right text-blue-600 font-medium">{m.demand || '-'}</td>
                                <td className="px-3 py-1.5 text-right text-amber-600">{m.planned || '-'}</td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="text-emerald-600 font-medium">{m.completed || '-'}</span>
                                  {pDates && <div className="text-[8px] text-emerald-400">{pDates}</div>}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className="text-teal-600 font-medium">{m.shipped || '-'}</span>
                                  {sDates && <div className="text-[8px] text-teal-400">{sDates}</div>}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_prd || 0) > 0 ? 'text-red-500 font-bold' : 'text-slate-300'}`}>
                                  {m.shortage_prd || '0'}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_shp || 0) > 0 ? 'text-red-500 font-bold' : 'text-slate-300'}`}>
                                  {m.shortage_shp || 0}
                                </td>
                                <td className={`px-3 py-1.5 text-right ${(m.shortage_pln || 0) > 0 ? 'text-orange-500 font-bold' : 'text-slate-300'}`}>
                                  {m.shortage_pln || 0}
                                </td>
                              </tr>
                              );
                            })}
                            {/* Divider after expanded section */}
                            <tr className="border-b-2 border-blue-200"><td colSpan={9} className="h-0"></td></tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Material Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="seat-breakdown">
              <div className="p-4 border-b border-slate-100">
                <SectionHeader title="Seat Materials" subtitle={`${reportData?.seats?.length || 0} types - Shortage cutoff: W${cw}`} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                      <th className="px-3 py-2 text-left font-semibold">Material</th>
                      <th className="px-3 py-2 text-right font-semibold">SPU</th>
                      <th className="px-3 py-2 text-right font-semibold">MDP<br/><span className="text-[7px] font-normal normal-case text-blue-300">seat (sp)</span></th>
                      <th className="px-3 py-2 text-right font-semibold">Produced<br/><span className="text-[7px] font-normal normal-case text-emerald-300">seat (sp)</span></th>
                      <th className="px-3 py-2 text-right font-semibold">Shipped<br/><span className="text-[7px] font-normal normal-case text-teal-300">seat (sp)</span></th>
                      <th className="px-3 py-2 text-right font-semibold text-red-400">Shortage<br/><span className="text-[7px] font-normal normal-case text-red-300">seat (sp)</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.seats?.map((s, i) => (
                      <tr key={s.code} className={i % 2 === 0 ? '' : 'bg-slate-50/30'}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-700">{s.description}</div>
                          <div className="text-[9px] text-slate-400">{s.code}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-violet-600 font-semibold">{s.spu}P</td>
                        <td className="px-3 py-2 text-right">
                          <div className="text-blue-600 font-bold">{s.demand.toLocaleString()}</div>
                          <div className="text-[8px] text-violet-500">{(s.demand * s.spu).toLocaleString()} sp</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="text-emerald-600 font-bold">{(s.completed_all || s.completed || 0).toLocaleString()}</div>
                          <div className="text-[8px] text-violet-500">{((s.completed_all || s.completed || 0) * s.spu).toLocaleString()} sp</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="text-teal-600 font-bold">{(s.shipped_all || s.shipped || 0).toLocaleString()}</div>
                          <div className="text-[8px] text-violet-500">{((s.shipped_all || s.shipped || 0) * s.spu).toLocaleString()} sp</div>
                        </td>
                        <td className={`px-3 py-2 text-right ${(s.shortage || 0) > 0 ? 'text-red-500' : 'text-slate-300'}`}>
                          <div className="font-bold">{s.shortage || 0}</div>
                          {(s.shortage || 0) > 0 && <div className="text-[8px] text-red-400">{((s.shortage || 0) * s.spu)} sp</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="other-breakdown">
              <div className="p-4 border-b border-slate-100">
                <SectionHeader title="Other Components" subtitle={`${reportData?.others?.length || 0} types`} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                      <th className="px-3 py-2 text-left font-semibold">Material</th>
                      <th className="px-3 py-2 text-right font-semibold">MDP</th>
                      <th className="px-3 py-2 text-right font-semibold">Produced</th>
                      <th className="px-3 py-2 text-right font-semibold">Shipped</th>
                      <th className="px-3 py-2 text-right font-semibold text-red-400">Shortage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.others?.map((o, i) => (
                      <tr key={o.code} className={i % 2 === 0 ? '' : 'bg-slate-50/30'}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-700">{o.description}</div>
                          <div className="text-[9px] text-slate-400">{o.code}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-blue-600 font-medium">{o.demand.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-emerald-600">{o.completed || 0}</td>
                        <td className="px-3 py-2 text-right text-teal-600">{o.shipped || 0}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${(o.shortage || 0) > 0 ? 'text-red-500' : 'text-slate-300'}`}>{o.shortage || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}




export default TrainReport;
